import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Paper,
    Snackbar,
    TextField,
    Typography
} from '@mui/material';
import BonDeCommande from '../models/BonDeCommande';
import axios from 'axios';
import DemandesDuBDC from './DemandesDuBDC';
import { BASE_URL } from '../api/config';

interface AdresseFacturationProps {
    bonDeCommande: BonDeCommande | null;
    onRefresh: () => void;
}

const AdresseFacturation: React.FC<AdresseFacturationProps> = ({ bonDeCommande, onRefresh }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [numFacture, setNumFacture] = useState('');

    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const [openCBModal, setOpenCBModal] = useState(false);
    const [paypalLink, setPaypalLink] = useState('');

    // États pour les modales de prévisualisation d'email
    const [openVirementModal, setOpenVirementModal] = useState(false);
    const [openPayeModal, setOpenPayeModal] = useState(false);

    // États pour les emails personnalisés
    const [virementEmail, setVirementEmail] = useState({
        subject: '',
        content: '',
        sendEmail: true
    });
    const [cbEmail, setCbEmail] = useState({
        subject: '',
        content: '',
        sendEmail: true
    });
    const [payeEmail, setPayeEmail] = useState({
        subject: '',
        content: '',
        sendEmail: true
    });

    // Ré-initialiser quand on change de BDC
    useEffect(() => {
        setSelectedFile(null);
        setNumFacture('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [bonDeCommande]);

    if (!bonDeCommande) {
        return <Typography>Sélectionnez un bon de commande pour voir les informations</Typography>;
    }

    // Sélection du texte sur survol (facultatif)
    const handleMouseOver = (event: React.MouseEvent) => {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(event.currentTarget);
        selection?.removeAllRanges();
        selection?.addRange(range);
    };

    const handleUploadClick = () => {
        if (!numFacture.trim()) {
            alert('Veuillez saisir un numéro de facture avant de sélectionner un fichier.');
            return;
        }
        fileInputRef.current?.click();
    };

    // IMPORTANT : ré-initialiser event.target.value = '' pour pouvoir re-sélectionner le même fichier
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files[0]) {
            setSelectedFile(files[0]);
        }
        event.target.value = '';
    };

    const handleCloseSnackbar = () => {
        setUploadSuccess(false);
        setUploadError('');
    };

    const uploadFacture = async () => {
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('bonDeCommandeId', bonDeCommande?.id.toString() || '');
        formData.append('numfacture', numFacture);

        try {
            const response = await axios.post(
                `${BASE_URL}/uploadFacture`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            setUploadSuccess(true);
            setUploadError('');
            onRefresh();
            console.log('Fichier uploadé avec succès:', response.data);
        } catch (error) {
            setUploadSuccess(false);
            setUploadError("Erreur lors de l'upload du fichier.");
            console.error("Erreur lors de l'upload du fichier:", error);
            throw error;
        }
    };

    const handleVirement = () => {
        if (!selectedFile) {
            alert("Veuillez d'abord sélectionner et uploader la facture PDF avant d'effectuer cette action.");
            return;
        }

        // Préparer le contenu par défaut de l'email
        const defaultSubject = "Votre facture est prête";
        const defaultContent = `Bonjour ${bonDeCommande?.nom || ''},

Votre commande est bien enregistrée dans notre système.
Vous trouverez ci-dessous le lien vous permettant d'accéder à votre facture (disponible aussi dans votre espace client).

[Lien vers la facture sera ajouté automatiquement]

Les frais de visa étant avancés pour votre compte, nous vous remercions de bien vouloir procéder au paiement sans délai.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com`;

        setVirementEmail({
            subject: defaultSubject,
            content: defaultContent,
            sendEmail: true
        });
        setOpenVirementModal(true);
    };

    const confirmVirement = async () => {
        try {
            await uploadFacture();
            await axios.put(
                `${BASE_URL}/bdc/${bonDeCommande.id}/virement`,
                {
                    subjectMail: virementEmail.subject,
                    mailContent: virementEmail.content,
                    sendEmail: virementEmail.sendEmail
                }
            );
            onRefresh();
            setOpenVirementModal(false);
            alert(virementEmail.sendEmail
                ? "BDC mis en état 'virement'. Mail envoyé au client pour virement."
                : "BDC mis en état 'virement' (sans envoi d'email).");
        } catch (error) {
            console.error("Erreur lors de la mise à jour du BDC en 'virement':", error);
            alert("Erreur lors de la mise à jour du BDC.");
        }
    };

    const handleOpenCBModal = () => {
        if (!selectedFile) {
            alert("Veuillez d'abord sélectionner et uploader la facture PDF avant d'effectuer cette action.");
            return;
        }
        setPaypalLink('');

        // Préparer le contenu par défaut de l'email
        const defaultSubject = "Votre facture et votre lien de paiement";
        const defaultContent = `Bonjour ${bonDeCommande?.nom || ''},

Votre commande est bien enregistrée dans notre système.
Afin que nos équipes la prennent en charge, vous trouverez ci-dessous le lien de paiement pour régler la facture.

[Le bouton de paiement sera ajouté automatiquement]

Le lien vous permettant d'accéder à votre facture (disponible aussi dans votre espace client) :
[Lien vers la facture sera ajouté automatiquement]

Dès réception de votre paiement, nos équipes prendront en charge vos demandes.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com`;

        setCbEmail({
            subject: defaultSubject,
            content: defaultContent,
            sendEmail: true
        });
        setOpenCBModal(true);
    };

    const handleCloseCBModal = () => {
        setOpenCBModal(false);
    };

    const handleEnvoyerLienCB = async () => {
        if (!selectedFile) {
            alert("Veuillez d'abord sélectionner et uploader la facture PDF avant d'effectuer cette action.");
            return;
        }

        try {
            await uploadFacture();
            await axios.put(
                `${BASE_URL}/bdc/${bonDeCommande.id}/envoyer-lien-cb`,
                {
                    paypalLink,
                    subjectMail: cbEmail.subject,
                    mailContent: cbEmail.content,
                    sendEmail: cbEmail.sendEmail
                }
            );
            onRefresh();
            alert(cbEmail.sendEmail
                ? "Lien CB envoyé avec succès !"
                : "BDC mis en 'attente paiement' (sans envoi d'email).");
            setOpenCBModal(false);
        } catch (error) {
            console.error("Erreur lors de l'envoi du lien CB:", error);
            alert("Erreur lors de l'envoi du lien CB.");
        }
    };

    const handlePasserEnPaye = () => {
        // Préparer le contenu par défaut de l'email
        const defaultSubject = "Nous avons bien reçu votre paiement.";
        const defaultContent = `Bonjour ${bonDeCommande?.nom || ''},

Nous accusons bonne réception de votre règlement et vous en remercions.

Votre dossier est pris en charge par nos équipes.
Des mails de suivis de dossier vous seront envoyés à chaque étape de votre commande.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com`;

        setPayeEmail({
            subject: defaultSubject,
            content: defaultContent,
            sendEmail: true
        });
        setOpenPayeModal(true);
    };

    const confirmPasserEnPaye = async () => {
        try {
            await axios.put(
                `${BASE_URL}/bdc/${bonDeCommande.id}/passer-paye`,
                {
                    subjectMail: payeEmail.subject,
                    mailContent: payeEmail.content,
                    sendEmail: payeEmail.sendEmail
                }
            );
            onRefresh();
            setOpenPayeModal(false);
            alert(payeEmail.sendEmail
                ? "BDC passé en payé avec succès !"
                : "BDC passé en payé (sans envoi d'email).");
        } catch (error) {
            console.error("Erreur lors de la mise à jour du BDC en payé:", error);
            alert("Erreur lors de la mise à jour du BDC.");
        }
    };

    return (
        <Paper style={{ padding: 20, marginTop: 20 }}>
            <Typography variant="h6" gutterBottom>
                Adresse de Facturation
            </Typography>
            
            {/* Cards pour les nouveaux champs */}
            <Grid container spacing={2} style={{ marginBottom: 20 }}>
                <Grid item xs={12} md={4}>
                    <Card style={{ backgroundColor: '#e3f2fd', minHeight: 100 }}>
                        <CardContent>
                            <Typography color="primary" variant="subtitle2" gutterBottom>
                                ID Utilisateur
                            </Typography>
                            <Typography variant="h5" style={{ fontWeight: 'bold' }}>
                                {bonDeCommande.id_utilisateur || 'N/A'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                    <Card style={{ backgroundColor: '#f3e5f5', minHeight: 100 }}>
                        <CardContent>
                            <Typography color="secondary" variant="subtitle2" gutterBottom>
                                ID Adresse Facturation
                            </Typography>
                            <Typography variant="h5" style={{ fontWeight: 'bold' }}>
                                {bonDeCommande.id_adresse_facturation || 'Non défini'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                    <Card style={{ backgroundColor: bonDeCommande.id_sage ? '#e8f5e9' : '#ffebee', minHeight: 100 }}>
                        <CardContent>
                            <Typography 
                                style={{ color: bonDeCommande.id_sage ? '#2e7d32' : '#c62828' }} 
                                variant="subtitle2" 
                                gutterBottom
                            >
                                ID Sage (Ancienne base Client)
                            </Typography>
                            <Typography variant="h5" style={{ fontWeight: 'bold' }}>
                                {bonDeCommande.id_sage || 'Pas de client Sage'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography>
                        Nom : <span onMouseOver={handleMouseOver}>{bonDeCommande.nom}</span>
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography>
                        Adresse ligne 1 :{" "}
                        <span onMouseOver={handleMouseOver}>{bonDeCommande.adresse_ligne1}</span>
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography>
                        Email : <span onMouseOver={handleMouseOver}>{bonDeCommande.email}</span>
                    </Typography>
                </Grid>
                {bonDeCommande.adresse_ligne2 && (
                    <>
                        <Grid item xs={6}>
                            <Typography>
                                Adresse ligne 2 :{" "}
                                <span onMouseOver={handleMouseOver}>{bonDeCommande.adresse_ligne2}</span>
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            {/* Espace vide pour alignement */}
                        </Grid>
                    </>
                )}
                <Grid item xs={6}>
                    <Typography>
                        Ville : <span onMouseOver={handleMouseOver}>{bonDeCommande.ville}</span>
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography>
                        Code Postal :{" "}
                        <span onMouseOver={handleMouseOver}>{bonDeCommande.code_postal}</span>
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography>
                        Pays : <span onMouseOver={handleMouseOver}>{bonDeCommande.pays}</span>
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography>
                        Téléphone :{" "}
                        <span onMouseOver={handleMouseOver}>{bonDeCommande.telephone}</span>
                    </Typography>
                </Grid>
            </Grid>

            <Box
                display="flex"
                flexDirection="column"
                alignItems="start"
                marginTop={4}
            >
                <Typography variant="body1" gutterBottom>
                    Numéro de facture :
                </Typography>
                <TextField
                    variant="outlined"
                    size="small"
                    value={numFacture}
                    onChange={(e) => setNumFacture(e.target.value)}
                    placeholder="Ex: 2023-001"
                />
            </Box>

            <Box
                display="flex"
                justifyContent="center"
                marginTop={4}
                flexDirection="column"
                alignItems="center"
            >
                <Typography variant="body1" gutterBottom>
                    Sélectionner la facture PDF (après avoir saisi le numéro de facture) :
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUploadClick}
                >
                    Choisir le Fichier
                </Button>
                <input
                    type="file"
                    accept=".pdf"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                {selectedFile && (
                    <Typography variant="body2" marginTop={1}>
                        Fichier sélectionné : {selectedFile.name}
                    </Typography>
                )}
            </Box>

            <Box marginTop={4}>
                <Typography variant="h6" gutterBottom>
                    Détails des Demandes Associées
                </Typography>
                {bonDeCommande && <DemandesDuBDC idBonDeCommande={bonDeCommande.id} />}
            </Box>

            <Box
                marginTop={4}
                display="flex"
                gap={2}
                justifyContent="center"
                flexWrap="wrap"
            >
                <Button variant="contained" color="info" onClick={handleVirement}>
                    Virement
                </Button>
                <Button variant="contained" color="secondary" onClick={handleOpenCBModal}>
                    Envoi lien CB
                </Button>
                <Button variant="contained" color="success" onClick={handlePasserEnPaye}>
                    Passer BDC en payé
                </Button>
            </Box>

            {/* Feedback Upload Facture */}
            {uploadSuccess && (
                <Snackbar open={uploadSuccess} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        Fichier uploadé avec succès !
                    </Alert>
                </Snackbar>
            )}
            {uploadError && (
                <Snackbar
                    open={Boolean(uploadError)}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                >
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {uploadError}
                    </Alert>
                </Snackbar>
            )}

            {/* Modal pour Virement */}
            <Dialog
                open={openVirementModal}
                onClose={() => setOpenVirementModal(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        height: '90vh',
                        maxHeight: '900px'
                    }
                }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 2 }}>
                    Prévisualisation de l'email - Virement
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            Destinataire : <strong>{bonDeCommande?.email}</strong>
                        </Typography>
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={virementEmail.sendEmail}
                                onChange={(e) => setVirementEmail({...virementEmail, sendEmail: e.target.checked})}
                                color="primary"
                            />
                        }
                        label="Envoyer l'email au client"
                        sx={{ mb: 3 }}
                    />

                    <TextField
                        label="Objet de l'email"
                        variant="outlined"
                        fullWidth
                        value={virementEmail.subject}
                        onChange={(e) => setVirementEmail({...virementEmail, subject: e.target.value})}
                        margin="normal"
                        disabled={!virementEmail.sendEmail}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Contenu de l'email"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={18}
                        value={virementEmail.content}
                        onChange={(e) => setVirementEmail({...virementEmail, content: e.target.value})}
                        margin="normal"
                        disabled={!virementEmail.sendEmail}
                        sx={{
                            '& .MuiInputBase-input': {
                                fontFamily: 'monospace',
                                fontSize: '14px'
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
                    <Button onClick={() => setOpenVirementModal(false)} size="large">
                        Annuler
                    </Button>
                    <Button
                        onClick={confirmVirement}
                        variant="contained"
                        color="primary"
                        size="large"
                    >
                        {virementEmail.sendEmail ? 'Confirmer et envoyer' : 'Confirmer sans envoyer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal pour Envoi lien CB */}
            <Dialog
                open={openCBModal}
                onClose={handleCloseCBModal}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        height: '90vh',
                        maxHeight: '900px'
                    }
                }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 2 }}>
                    Prévisualisation de l'email - Lien CB
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            Destinataire : <strong>{bonDeCommande?.email}</strong>
                        </Typography>
                    </Box>

                    <TextField
                        margin="normal"
                        label="Lien PayPal/CB"
                        variant="outlined"
                        fullWidth
                        value={paypalLink}
                        onChange={(e) => setPaypalLink(e.target.value)}
                        helperText="Ce lien sera intégré dans l'email sous forme de bouton"
                        sx={{ mb: 2 }}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={cbEmail.sendEmail}
                                onChange={(e) => setCbEmail({...cbEmail, sendEmail: e.target.checked})}
                                color="primary"
                            />
                        }
                        label="Envoyer l'email au client"
                        sx={{ mb: 3 }}
                    />

                    <TextField
                        label="Objet de l'email"
                        variant="outlined"
                        fullWidth
                        value={cbEmail.subject}
                        onChange={(e) => setCbEmail({...cbEmail, subject: e.target.value})}
                        margin="normal"
                        disabled={!cbEmail.sendEmail}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Contenu de l'email"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={16}
                        value={cbEmail.content}
                        onChange={(e) => setCbEmail({...cbEmail, content: e.target.value})}
                        margin="normal"
                        disabled={!cbEmail.sendEmail}
                        sx={{
                            '& .MuiInputBase-input': {
                                fontFamily: 'monospace',
                                fontSize: '14px'
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
                    <Button onClick={handleCloseCBModal} size="large">
                        Annuler
                    </Button>
                    <Button
                        onClick={handleEnvoyerLienCB}
                        variant="contained"
                        color="primary"
                        size="large"
                    >
                        {cbEmail.sendEmail ? 'Confirmer et envoyer' : 'Confirmer sans envoyer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal pour Passer en Payé */}
            <Dialog
                open={openPayeModal}
                onClose={() => setOpenPayeModal(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        height: '90vh',
                        maxHeight: '900px'
                    }
                }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 2 }}>
                    Prévisualisation de l'email - Confirmation de paiement
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            Destinataire : <strong>{bonDeCommande?.email}</strong>
                        </Typography>
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={payeEmail.sendEmail}
                                onChange={(e) => setPayeEmail({...payeEmail, sendEmail: e.target.checked})}
                                color="primary"
                            />
                        }
                        label="Envoyer l'email au client"
                        sx={{ mb: 3 }}
                    />

                    <TextField
                        label="Objet de l'email"
                        variant="outlined"
                        fullWidth
                        value={payeEmail.subject}
                        onChange={(e) => setPayeEmail({...payeEmail, subject: e.target.value})}
                        margin="normal"
                        disabled={!payeEmail.sendEmail}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Contenu de l'email"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={18}
                        value={payeEmail.content}
                        onChange={(e) => setPayeEmail({...payeEmail, content: e.target.value})}
                        margin="normal"
                        disabled={!payeEmail.sendEmail}
                        sx={{
                            '& .MuiInputBase-input': {
                                fontFamily: 'monospace',
                                fontSize: '14px'
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
                    <Button onClick={() => setOpenPayeModal(false)} size="large">
                        Annuler
                    </Button>
                    <Button
                        onClick={confirmPasserEnPaye}
                        variant="contained"
                        color="primary"
                        size="large"
                    >
                        {payeEmail.sendEmail ? 'Confirmer et envoyer' : 'Confirmer sans envoyer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default AdresseFacturation;